import type { Ref } from 'vue'
import { computed, type ComputedRef, inject } from 'vue'
import type { CollectionPlayerWithPages, CollectionGroupWithPages } from '@ppt-conversion-selector/types/model.type'
import type { CollectionPage } from '@collection-player/types/collection-player.type'
import { createNodeId } from '@canvas-builder/util/canvas.util'
import { type ComponentNode, type SectionListProps, ComponentTypes } from '@canvas-builder/types/canvas'
import { findFirstSlide } from '@ppt-conversion-selector/utils/data.util'
import type { Api } from '@ppt-conversion-selector/stores/app.store'
import type { CanvasCreateRequest, CanvasRetrieve } from '@openapi'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types.ts'
import type { PitcherInfo } from '@lib/types/app'
import { transformToSmallThumbnail } from '@ppt-conversion-selector/utils/thumbnail.util'

type UseCanvasPayload = {
  t: (key: string, values?: Record<string, string | number>) => string
  api: Api
  collection: Ref<CollectionPlayerWithPages>
  tags?: string[]
}

const getImageDimensions = async (imageUrl: string) => {
  const TARGET_WIDTH = 1160

  return new Promise<{ width: number; height: number }>((resolve, reject) => {
    const img = new Image()

    img.onload = () => {
      const aspectRatio = img.height / img.width
      const newWidth = TARGET_WIDTH
      const newHeight = Math.round(TARGET_WIDTH * aspectRatio)

      resolve({ width: newWidth, height: newHeight })
    }

    img.onerror = reject
    img.src = imageUrl
  })
}

export const useCanvas = ({ t, api, collection, tags }: UseCanvasPayload) => {
  const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
    'launchDarkly',
    computed(() => ({})),
  )

  const pitcherInfo = inject<ComputedRef<PitcherInfo>>(
    'pitcherInfo',
    computed(() => undefined),
  )

  const createSectionPayload = async (
    item: CollectionPage,
    details: { title: string; id: number; sequence: number },
    group: CollectionGroupWithPages,
    image?: { width?: number; height?: number; url: string },
  ): Promise<Omit<CanvasCreateRequest, 'instance_id'>> => {
    const slideImage = {
      width: image?.width,
      height: image?.height,
      url: image?.url ?? item.slide.url,
    }

    if (!slideImage?.url) {
      throw new Error('Slide image URL is required: ' + item.file.id + ' / ' + item.slide.index)
    }

    if (!slideImage?.width || !slideImage?.height) {
      const { width, height } = await getImageDimensions(slideImage.url)

      slideImage.width = width
      slideImage.height = height
    }

    return {
      name: details.title
        ? `${details.title} (${item.slide.index + 1})`
        : t('canvasUI.pptConversionSelector.slideNumber', { number: item.slide.index + 1 }),
      content: {
        data: [
          {
            id: createNodeId(ComponentTypes.Scribble),
            data: launchDarkly.value?.convert_pptx_to_section_with_scribble_pdfjs
              ? {
                  content_background: {
                    file_id: item.file.id,
                    page_id: details.id,
                  },
                }
              : {},
            type: ComponentTypes.Scribble,
            style: {
              height: `${(slideImage.height! / slideImage.width!) * 1198}px`,
              width: `1198px`,
              backgroundSize: 'cover',
              backgroundImage: `url(${slideImage.url})`,
              backgroundRepeat: 'no-repeat',
              backgroundPositionX: 'left',
              backgroundPositionY: 'top',
            },
            children: [],
            tracking_id: details.title,
          },
        ],
      },
      theme: {
        fluid: false,
        canvas_bg_color: '#ffffff',
      },
      is_draft: false,
      is_section: true,
      metadata: {
        ...item.file.metadata,
        pptx_data: {
          slides: [details],
        },
        search_data: item.file.metadata?.pptx_data?.slides?.[item.slide.index]?.['title'] ?? {},
        slide_preview: item.slide.url,
        presentationname: item.file.name,
        sequence: item.slide.index,
        section: group.name,
        pitcher_is_original_content: true,
        pitcher_source_file_id: item.file.id,
        pitcher_slide_id: details.id,
      },
      thumbnail_url: item.slide.url
        ? await transformToSmallThumbnail(item.slide.url, pitcherInfo.value?.accessToken)
        : item.slide.url,
    }
  }

  const updateSection = async (
    canvasId: string,
    item: CollectionPage,
    details: { title: string; id: number; sequence: number },
    group: CollectionGroupWithPages,
    image?: { width?: number; height?: number; url: string },
  ) => api.updateCanvas({ id: canvasId, ...(await createSectionPayload(item, details, group, image)) })

  const createSection = async (
    item: CollectionPage,
    details: { title: string; id: number; sequence: number },
    group: CollectionGroupWithPages,
    image?: { width?: number; height?: number; url: string },
  ) => {
    return api.createCanvas(await createSectionPayload(item, details, group, image))
  }

  const saveTemplate = (savedSlides: CanvasRetrieve[], slidesById: Record<number, CanvasRetrieve>) => {
    const sectionLists: ComponentNode<SectionListProps>[] = []
    const sections: CanvasRetrieve[] = []

    const { file } = findFirstSlide(collection.value) ?? {}

    collection.value.groups.forEach((group) => {
      sectionLists.push({
        type: ComponentTypes.SectionList,
        id: createNodeId(ComponentTypes.SectionList),
        tracking_id: group.name,
        data: {
          sections: group.slides.map((_slide) => {
            const id = _slide.file.metadata?.pptx_data?.slides[_slide.slide.index].id
            const slide = slidesById[parseInt(id)]

            if (!slide) {
              throw new Error('Slide not found')
            }

            sections.push(slide)

            return {
              id: slide.id,
            }
          }),
          animation_in: 'none',
        },
        children: [],
        when_used_in_section: {
          is_editable: false,
          is_removable: false,
        },
        visible: {
          edit_mode: true,
          presentation_mode: true,
          web: true,
          pdf: true,
        },
        autofill: 'off',
        autofill_content_types: null,
      })
    })

    const templateCanvasPayload = {
      name: collection.value.name,
      content: {
        data: sectionLists,
        overrides: {},
      },
      theme: {
        fluid: true,
      },
      is_template: true,
      tags: tags ?? [],
      metadata: file?.metadata ?? {},
      sections,
    }

    return api.createCanvas(templateCanvasPayload)
  }

  const fetchCanvases = async (ids: string[]) => api.getCanvases({ id__in: ids.join(',') })
  const deleteCanvas = async (id: string) => api.deleteCanvas(id)

  return {
    createSection,
    saveTemplate,
    fetchCanvases,
    updateSection,
    deleteCanvas,
  }
}
